"use client";

import { FormEvent, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, getApp } from "@/lib/firebaseClient";
import TurnstileWidget from "@/components/TurnstileWidget";
import { verifyTurnstile } from "@/lib/turnstile";
import { markProvisioning, readProvisioning, authDiag } from "@/lib/provisioning";

type Step = "form" | "creating" | "setting_up" | "done" | "error";

function mapFirebaseError(code: string | undefined): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "Ese email ya está registrado. ¿Querés iniciar sesión?";
    case "auth/invalid-email":
      return "El email no es válido.";
    case "auth/weak-password":
      return "La contraseña es muy débil. Usá al menos 6 caracteres.";
    case "functions/already-exists":
      return "Este usuario ya tiene una cervecería registrada.";
    case "functions/not-found":
      return "El código de voucher no existe o ya fue usado.";
    default:
      return "Ocurrió un error. Revisá los datos e intentá de nuevo.";
  }
}

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent";

export default function SignUpPage() {
  const router = useRouter();

  // Datos mínimos para entrar rápido. El resto (teléfono, ubicación, datos
  // fiscales) se completa DESPUÉS desde la app, con un recordatorio suave.
  const [firstName, setFirstName]     = useState("");
  const [breweryName, setBreweryName] = useState("");

  // Credenciales
  const [email, setEmail]                     = useState("");
  const [password, setPassword]               = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword]       = useState(false);
  const [googleLoading, setGoogleLoading]     = useState(false);

  // Voucher (opcional, colapsable)
  const [voucherCode, setVoucherCode] = useState("");
  const [showVoucher, setShowVoucher] = useState(false);

  const [step, setStep]   = useState<Step>("form");
  const [error, setError] = useState<string | null>(null);
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0); // bump para remontar el widget (token de un solo uso)
  const [alreadyAuthed, setAlreadyAuthed] = useState(false);
  const [authChecked, setAuthChecked]     = useState(false); // evita parpadeo

  // Detectar si el usuario ya está autenticado (redirigido desde signin sin brewery)
  useEffect(() => {
    const auth = getAuth();
    if (!auth) { setAuthChecked(true); return; }
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Si ya tiene cervecería, no tiene nada que hacer acá → al panel.
        try {
          const token = await u.getIdTokenResult();
          if (token.claims.breweryId) { router.replace("/dashboard"); return; }
        } catch { /* si falla, seguimos al paso de terminar registro */ }
        // Si ACABAMOS de crear la cervecería (señal activa) pero el claim aún no
        // propagó, NO mostramos el cartel "¡Ya casi estás!" (era lo que parpadeaba):
        // volvemos al panel y dejamos que AuthGuard espere estable hasta el claim.
        if (readProvisioning()) {
          authDiag("signup", "señal activa sin claim → /dashboard (AuthGuard espera)");
          router.replace("/dashboard");
          return;
        }
        setAlreadyAuthed(true);
        if (u.displayName) {
          setFirstName((prev) => prev || u.displayName!.trim().split(" ")[0] || "");
        }
        if (u.email) setEmail((prev) => prev || u.email || "");
      }
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  // Crea la cervecería automáticamente (sin formulario) y entra al panel.
  // Es el corazón del "un toque y adentro" del registro con Google/Apple.
  const autoProvisionAndEnter = async (user: any) => {
    setStep("setting_up");
    try {
      // ¿Ya tiene cervecería (usuario que vuelve o miembro invitado)? → al panel directo.
      const token = await user.getIdTokenResult();
      if (token.claims.breweryId) {
        router.replace("/dashboard");
        return;
      }

      const app = getApp();
      if (!app) throw new Error("Firebase no disponible.");
      const functions = getFunctions(app, "southamerica-east1");
      const registerBrewery = httpsCallable(functions, "registerBrewery");

      const dn = (user.displayName ?? "").trim();
      const first = dn.split(" ")[0] || "";
      const defaultName = first ? `Cervecería de ${first}` : "Mi cervecería";

      authDiag("signup", "llamando registerBrewery (social)", { defaultName });
      const res: any = await registerBrewery({
        breweryName: defaultName,
        ownerDisplayName: dn || (user.email ?? "").split("@")[0] || "Dueño",
        country: "AR", // default editable en Ajustes. TODO: geolocalización por IP.
      });

      // El servidor ya creó la cervecería y devolvió su id: DEJAMOS LA SEÑAL.
      // Con esto AuthGuard sabe que la cuenta existe aunque el claim todavía no
      // haya propagado al token → espera quieto en vez de rebotar a /signup.
      const newBreweryId = res?.data?.breweryId as string | undefined;
      if (newBreweryId) markProvisioning(newBreweryId);
      authDiag("signup", "registerBrewery OK, señal dejada", { newBreweryId });

      setBreweryName(defaultName); // para la pantalla de bienvenida

      // Esperar ACTIVAMENTE a que el custom claim `breweryId` aparezca en el token
      // (evita el loop dashboard↔signup por claims que todavía no propagaron).
      let hasClaim = false;
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, 1200));
        const t = await user.getIdTokenResult(true);
        if ((t.claims as any)?.breweryId) { hasClaim = true; break; }
      }
      authDiag("signup", "fin espera claim en signup", { hasClaim });

      setStep("done");
      // Recarga LIMPIA (no SPA) para que la app arranque con el token nuevo ya
      // con el claim breweryId. Si el claim aún no llegó, la señal `provisioning`
      // hace que AuthGuard espere estable (sin parpadeo) hasta que propague.
      setTimeout(() => { window.location.assign("/dashboard"); }, hasClaim ? 800 : 1500);
    } catch (err: any) {
      if (err?.code === "functions/already-exists") {
        router.replace("/dashboard");
        return;
      }
      console.error("[signup auto]", err);
      setError("No se pudo terminar el registro. Reintentá.");
      setStep("form");
    }
  };

  const handleSocialSignUp = async (provider: GoogleAuthProvider | OAuthProvider, providerName: string) => {
    const auth = getAuth();
    if (!auth) return;
    setGoogleLoading(true);
    setError(null);
    try {
      const { user } = await signInWithPopup(auth, provider);
      // Un toque y adentro: creamos la cervecería sola y vamos al panel.
      await autoProvisionAndEnter(user);
    } catch (err: any) {
      const code = err?.code;
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setError(`No se pudo continuar con ${providerName}. Intentá de nuevo.`);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    handleSocialSignUp(provider, "Google");
  };

  const handleAppleSignUp = () => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    provider.setCustomParameters({ locale: "es" });
    handleSocialSignUp(provider, "Apple");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError("Ingresá tu nombre.");
      return;
    }
    if (!breweryName.trim() || breweryName.trim().length < 2) {
      setError("El nombre de la cervecería debe tener al menos 2 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    // Verificación anti-bot (Turnstile)
    if (!tsToken) {
      setError("Completá la verificación de seguridad.");
      return;
    }
    const human = await verifyTurnstile(tsToken);
    if (!human) {
      setError("La verificación de seguridad falló. Probá de nuevo.");
      setTsToken(null);
      setTsKey((k) => k + 1);
      return;
    }

    setStep("creating");

    const auth = getAuth();
    if (!auth) {
      setError("Firebase no disponible. Recargá la página.");
      setStep("error");
      return;
    }

    const displayName = firstName.trim();

    try {
      let user = auth.currentUser;

      if (user) {
        // Ya autenticado (vía Google/Apple) — solo actualizar perfil si falta
        if (displayName && !user.displayName) {
          await updateProfile(user, { displayName });
        }
      } else {
        // Registro normal con email/password
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        user = cred.user;
        if (displayName) await updateProfile(user, { displayName });
      }

      setStep("setting_up");

      // Crear la cervecería via Cloud Function (solo lo mínimo; el resto se
      // completa después desde Ajustes con el recordatorio suave).
      const app = getApp();
      if (!app) throw new Error("Firebase no disponible.");
      const functions = getFunctions(app, "southamerica-east1");
      const registerBrewery = httpsCallable<
        {
          breweryName: string;
          ownerDisplayName: string;
          country: string;
          voucherCode?: string;
        },
        { breweryId: string; breweryName: string; trialDays: number }
      >(functions, "registerBrewery");

      authDiag("signup", "llamando registerBrewery (email)", { breweryName: breweryName.trim() });
      const res = await registerBrewery({
        breweryName:      breweryName.trim(),
        ownerDisplayName: displayName,
        country:          "AR", // default editable en Ajustes. TODO: geolocalización por IP.
        voucherCode:      voucherCode.trim().toUpperCase() || undefined,
      });

      // Dejamos la señal para que AuthGuard espere estable el claim (igual que el
      // camino social) → sin parpadeo dashboard↔signup mientras propaga.
      const newBreweryId = res?.data?.breweryId as string | undefined;
      if (newBreweryId) markProvisioning(newBreweryId);
      authDiag("signup", "registerBrewery OK (email), señal dejada", { newBreweryId });

      setStep("done");

      // Recarga LIMPIA a /dashboard (no SPA) para arrancar con el token fresco.
      setTimeout(() => { window.location.assign("/dashboard"); }, 1000);
    } catch (err: any) {
      console.error("[signup]", err);
      // El token Turnstile es de un solo uso: reiniciamos el widget para reintentar.
      setTsToken(null);
      setTsKey((k) => k + 1);
      const code = err?.code as string | undefined;
      setError(mapFirebaseError(code) + (err?.message && !code ? `: ${err.message}` : ""));
      setStep("error");
    }
  };

  // ─── Pantalla de éxito ────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="text-5xl mb-4">🍺</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Bienvenido a Cuaderno Cervecero!
          </h1>
          <p className="text-gray-500 mb-1">
            Tu cervecería <strong className="text-gray-700">{breweryName}</strong> ya está lista.
          </p>
          <p className="text-sm text-gray-400">Redirigiendo al panel…</p>
          <div className="mt-6 flex justify-center">
            <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // ─── Pantalla de loading ──────────────────────────────────────────────────
  if (step === "creating" || step === "setting_up") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {step === "creating" ? "Creando tu cuenta…" : "Configurando tu cervecería…"}
          </h2>
          <p className="text-sm text-gray-400">
            {step === "creating"
              ? "Registrando tus datos de acceso"
              : "Preparando tu panel de control"}
          </p>
        </div>
      </div>
    );
  }

  // ─── Autenticado con Google/Apple pero sin cervecería (limbo) → un solo botón ──
  if (authChecked && alreadyAuthed) {
    const cu = getAuth()?.currentUser;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center space-y-5">
          <div className="text-5xl">🍺</div>
          <h1 className="text-2xl font-bold text-gray-900">¡Ya casi estás!</h1>
          <p className="text-gray-500">
            Iniciaste sesión como{" "}
            <strong className="text-gray-700">{cu?.displayName || cu?.email}</strong>.
            {" "}Creamos tu cervecería y entrás al toque — después le ponés el nombre y los
            datos que quieras desde Ajustes.
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="button"
            onClick={() => { const u = getAuth()?.currentUser; if (u) autoProvisionAndEnter(u); }}
            className="w-full rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3"
          >
            Crear mi cervecería y entrar
          </button>
          <button
            type="button"
            onClick={() => getAuth()?.signOut()}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Usar otra cuenta
          </button>
        </div>
      </div>
    );
  }

  // ─── Formulario corto (registro con email/contraseña) ──────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-6">
      {/* Botón volver al inicio */}
      <div className="fixed top-4 left-4">
        <a
          href="/"
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 border border-gray-200 hover:border-gray-400 bg-white rounded-lg px-3 py-1.5 transition-colors"
        >
          ← Inicio
        </a>
      </div>

      <div className="max-w-md w-full">

        {/* Header */}
        <div className="text-center mb-5">
          <div className="text-4xl mb-1">🍺</div>
          <h1 className="text-2xl font-bold text-gray-900">Cuaderno Cervecero</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Creá tu cuenta en segundos. 30 días de prueba gratis.
          </p>
        </div>

        {/* Spinner mientras se verifica la sesión (evita parpadeo) */}
        {!authChecked && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5 ${!authChecked ? "hidden" : ""}`}>
          <h2 className="text-lg font-semibold text-gray-800">Crear cuenta</h2>

          {/* Botones sociales */}
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {googleLoading ? "Conectando..." : "Registrarte con Google"}
            </button>

            <button
              type="button"
              onClick={handleAppleSignUp}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60"
            >
              <svg width="17" height="18" viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-38.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 71 0 130.5 46.4 175 46.4 42.3 0 109.3-49 192.3-49 31 0 113.3 2.6 168.2 79zm-225.1-234.7c31.6-37.4 54.3-89.2 54.3-140.9 0-7.1-.6-14.3-1.9-20.1-51.5 2-112.5 34.5-148.5 75.5-28.5 32.4-55.1 83.6-55.1 136 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 46.5 0 102.5-30.4 135.7-69.9z"/>
              </svg>
              {googleLoading ? "Conectando..." : "Registrarte con Apple"}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">o con email</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tu nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text" required autoFocus
                placeholder="Ej: Carlos"
                className={inputCls}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            {/* Nombre de la cervecería */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la cervecería <span className="text-red-500">*</span>
              </label>
              <input
                type="text" required
                placeholder="Ej: Cervecería La Pirata"
                className={inputCls}
                value={breweryName}
                onChange={(e) => setBreweryName(e.target.value)}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email" required
                placeholder="hola@micerveceria.com"
                autoComplete="username"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Contraseñas */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} required minLength={6}
                    name="password"
                    placeholder="Mín. 6"
                    autoComplete="new-password"
                    className={inputCls + " pr-10"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword
                      ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"} required minLength={6}
                  name="confirm-password"
                  placeholder="Repetir"
                  autoComplete="new-password"
                  className={inputCls}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {/* Voucher (colapsable) */}
            <div>
              {!showVoucher ? (
                <button
                  type="button"
                  onClick={() => setShowVoucher(true)}
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                >
                  🎟 Tengo un código de acceso fundador
                </button>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código de acceso fundador
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: CC-AB3X"
                    className={`${inputCls} uppercase tracking-wider`}
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  />
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
                {error.includes("iniciar sesión") && (
                  <Link href="/signin" className="text-sm text-red-700 underline font-medium mt-1 block">
                    Ir a Iniciar sesión →
                  </Link>
                )}
              </div>
            )}

            {/* Verificación anti-bot */}
            <div className="flex justify-center">
              <TurnstileWidget
                key={tsKey}
                onToken={setTsToken}
                onExpire={() => setTsToken(null)}
              />
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={step !== "form" && step !== "error"}
              className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Crear mi cervecería →
            </button>

            {/* Nota: el resto de los datos se completan después */}
            <p className="text-xs text-gray-400 text-center">
              Después completás teléfono, ubicación y datos fiscales desde Ajustes —
              sin apuro y cuando quieras.
            </p>
          </form>
        </div>

        {/* Link a login */}
        <p className="text-center text-sm text-gray-500 mt-4">
          ¿Ya tenés cuenta?{" "}
          <Link href="/signin" className="text-amber-600 hover:text-amber-700 font-medium">
            Iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
